import { z } from 'zod';
/**
 * M16 — les cinq cases « méthodes utilisées » que la **v1** a écrites, dont `COMCOM` : gardées
 * ici pour la relecture des contrôles déjà transmis, jamais proposées à la saisie. Les méthodes
 * d'aujourd'hui sont `SURVEY_METHODS` (`survey-v2.ts`), qui n'en compte plus que quatre — la
 * ComCom n'est pas un moyen de relevé mais la collectivité donneuse d'ordre (#117).
 */
export const SURVEY_METHODS_V1 = ['COMCOM', 'PHOTOS', 'CAMERA', 'TOPO', 'FUMEE'];
/**
 * M16 — le tri-état de la fiche : l'agent enregistre une **présomption** (« fosse suspectée »),
 * jamais un booléen aplati. L'aplatir détruirait l'information la plus fragile du relevé, et le
 * CDC en fait une mention de signature.
 *
 * ⚠️ `absent` est un **constat négatif** — l'agent a constaté qu'il n'y a rien —, et c'est ce que
 * les écrans affichent « Éléments manquants », les mots du terrain (#119, confirmé le
 * 09/09/2026). Ce n'est pas « je n'ai pas pu observer » : cette place-là est déjà prise, par
 * `suspected` et par l'absence de valeur.
 */
export const presenceAssessmentSchema = z.enum(['observed', 'suspected', 'absent']);
/**
 * Les trois crans **dans les mots du terrain**, une seule fois pour les deux applications.
 *
 * ⚠️ Ils vivent dans le domaine pour la raison qui y a mis `INSPECTION_RESULT_LABELS` : leurs
 * lecteurs ne peuvent pas s'importer l'un l'autre. La tablette affichait « Suspecté », le bureau
 * « Soupçonné » — l'agent tapait sur un segment, le valideur lisait un autre mot, et ni l'un ni
 * l'autre ne pouvait confirmer qu'il s'agissait du même constat. C'est exactement ce que le
 * renommage de `absent` voulait éviter.
 *
 * ⚠️ « Éléments manquants » est un **constat négatif** — l'agent a vu qu'il n'y avait rien —, et
 * la phrase qui part au client (#119, confirmé le 09/09/2026). Ce n'est pas « je n'ai pas pu
 * observer » : cette place-là est prise par « Suspecté » et par l'absence de valeur.
 */
export const PRESENCE_ASSESSMENT_LABELS = {
    observed: 'Constaté',
    suspected: 'Suspecté',
    absent: 'Éléments manquants',
};
/** Un décompte : entier, jamais négatif. Nommé une fois, la fiche en porte sept. */
const count = z.number().int().min(0);
const presence = presenceAssessmentSchema.nullish();
/**
 * M16 — le relevé de terrain, **version 1**, **figé** : un contrôle déjà transmis le porte, et
 * `readProposalPayload` le relit tel quel avant de le monter en v2 (`upgradeSurvey`). Toute
 * évolution du relevé se fait dans `survey-v2.ts` — modifier ce schéma rendrait illisible ce
 * qu'il a lui-même écrit.
 *
 * Sous-ensemble S4 : la matrice intérieure (`indoorFixtures`, `uncheckedFixtures`, #258) et les
 * surfaces raccordées (`connectedSurfaces`) sont entrées en v2,
 * comme clés optionnelles — le schéma étant `loose`, rien de ce qui a été saisi n'en a souffert.
 *
 * **Rien n'est obligatoire, et c'est le cœur de la fiche.** Une tablette peut porter une version
 * plus récente que l'API le temps d'un déploiement : valider en `strict` reviendrait à **effacer
 * un relevé de terrain** parce qu'il contient un champ de trop. On garde tout, on signale, le
 * back-office tranche (M21). D'où `looseObject` à chaque niveau — s'arrêter au premier laisserait
 * les sections jeter en silence.
 *
 * ⚠️ Les clés sont en **camelCase**. La règle `snake_case` vaut pour les colonnes SQL ; ce JSONB
 * n'est écrit et relu que par TypeScript, et un mapping de casse serait du code mort.
 */
export const surveyV1Schema = z.looseObject({
    methodsUsed: z.array(z.string()).optional(),
    dwelling: z
        .looseObject({
        constructionYear: z.number().int().nullish(),
        connectedDwellingsCount: count.nullish(),
        mainRoomsCount: count.nullish(),
        occupantsCount: count.nullish(),
        residenceType: z.string().nullish(),
        presenceYears: count.nullish(),
        presenceMonths: count.nullish(),
    })
        .optional(),
    plot: z
        .looseObject({
        areaM2: z.number().nonnegative().nullish(),
        drinkingWaterSupply: z.string().nullish(),
        publicNetworkType: z.string().nullish(),
        wastewaterProduction: z.string().nullish(),
        well: z
            .looseObject({
            present: z.boolean().nullish(),
            uses: z.array(z.string()).optional(),
            declared: z.boolean().nullish(),
        })
            .optional(),
    })
        .optional(),
    connectionEquipment: z
        .looseObject({
        septicTank: presence,
        greaseTrap: presence,
        basement: z.looseObject({ present: z.boolean().nullish() }).optional(),
        wastewaterCheckValve: z
            .looseObject({ exists: z.boolean().nullish(), required: z.boolean().nullish() })
            .optional(),
        stormwaterCheckValve: z
            .looseObject({ exists: z.boolean().nullish(), required: z.boolean().nullish() })
            .optional(),
        wastewaterPump: z
            .looseObject({
            present: z.boolean().nullish(),
            extent: z.enum(['full', 'partial']).nullish(),
        })
            .optional(),
        stormwaterPump: z
            .looseObject({
            present: z.boolean().nullish(),
            extent: z.enum(['full', 'partial']).nullish(),
        })
            .optional(),
        wastewaterEasements: z
            .looseObject({
            present: z.boolean().nullish(),
            kind: z.enum(['private', 'public']).nullish(),
        })
            .optional(),
        wastewaterConnectionBox: z
            .looseObject({ present: z.boolean().nullish(), position: z.string().nullish() })
            .optional(),
        stormwaterConnectionBox: z
            .looseObject({ present: z.boolean().nullish(), position: z.string().nullish() })
            .optional(),
        wastewaterTrap: z
            .looseObject({ present: z.boolean().nullish(), position: z.string().nullish() })
            .optional(),
    })
        .optional(),
});
/**
 * Le numéro que portent les relevés écrits par ce schéma-ci. La version **courante** est
 * `SURVEY_VERSION` (`survey-v2.ts`) : c'est elle qu'une tablette à jour annonce, celle-ci ne
 * sert plus qu'à reconnaître ce qui a déjà été transmis.
 */
export const SURVEY_V1_VERSION = 1;
//# sourceMappingURL=survey-v1.js.map